/**
 * DR and EP assemblers (slice C). Build a dead reckoning or an estimated
 * position as a proper PlotObject construction that preserves every element:
 * the start, the water track, the DR, and for an EP the tidal vector, the EP
 * itself, and the ground track. Editing any input can then recompute the
 * result, and the construction can be explained, reviewed, and marked.
 *
 * The maths is delegated to the tested nav functions (deadReckon,
 * estimatedPosition), so this layer only assembles objects and wires the
 * parent references. The two are kept apart because the navigational meanings
 * differ and must not blur:
 *   - water track: movement through the water (course steered, STW, time). No tide.
 *   - DR: the position from the water track alone.
 *   - tidal vector: the tide's effect over the same interval, applied from the DR.
 *   - EP: the DR with the tide (and optionally leeway) applied.
 *   - ground track: the resulting track over the ground, start to EP.
 */
import {
  deadReckon,
  estimatedPosition,
  rhumbBearingDeg,
  rhumbDistanceNm,
  epUncertaintyEllipse,
  ellipsePolygon,
} from '@/nav';
import type { PositionPoint, Vector, TimeStamp, TimeInterval, Zone } from './objects';

export interface BuildDRParams {
  start: [number, number];
  /** Heading through the water, degrees true. */
  headingDeg: number;
  /** Speed through the water, knots. */
  speedKnots: number;
  hours: number;
  time?: TimeStamp;
  startLabel?: string;
  newId: (prefix: string) => string;
}

export interface BuiltDR {
  start: PositionPoint;
  waterTrack: Vector;
  dr: PositionPoint;
}

export interface BuildEPParams extends BuildDRParams {
  /** Tidal set: the direction the stream flows TOWARDS, degrees true. */
  tidalSet: number;
  /** Tidal rate, knots. */
  tidalRate: number;
  /** Optional leeway angle, degrees. */
  leewayDeg?: number;
}

export interface BuiltEP {
  start: PositionPoint;
  waterTrack: Vector;
  dr: PositionPoint;
  tidalVector: Vector;
  ep: PositionPoint;
  groundTrack: Vector;
  /** Growing uncertainty ellipse around the EP (honest doubt, not measured). */
  ellipse: Zone;
}

function makeInterval(hours: number, time?: TimeStamp): TimeInterval {
  const startIso = time?.iso ?? '';
  let endIso = '';
  if (time?.iso) {
    const d = new Date(time.iso);
    if (!Number.isNaN(d.getTime())) {
      endIso = new Date(d.getTime() + hours * 3600_000).toISOString();
    }
  }
  return { startIso, endIso, hours };
}

export function buildDR(params: BuildDRParams): BuiltDR {
  const { start, headingDeg, speedKnots, hours, time, newId } = params;
  const interval = makeInterval(hours, time);

  const startPt: PositionPoint = {
    id: newId('start'),
    kind: 'position_point',
    pointType: 'assumed',
    position: start,
    time,
    label: params.startLabel ?? 'Origin',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };

  const waterTrack: Vector = {
    id: newId('wt'),
    kind: 'water_track',
    start: startPt.id,
    directionDeg: headingDeg,
    rateKnots: speedKnots,
    interval,
    source: { method: 'manual' },
    parents: [startPt.id],
    children: [],
    userPlaced: false,
  };

  const drPos = deadReckon(start, headingDeg, speedKnots, hours);
  const dr: PositionPoint = {
    id: newId('dr'),
    kind: 'position_point',
    pointType: 'dr',
    position: drPos,
    time,
    label: 'DR',
    source: { method: 'manual' },
    parents: [startPt.id, waterTrack.id],
    children: [],
    userPlaced: false,
  };

  return { start: startPt, waterTrack, dr };
}

export function buildEP(params: BuildEPParams): BuiltEP {
  const { start, headingDeg, speedKnots, hours, tidalSet, tidalRate, leewayDeg, time, newId } =
    params;
  const interval = makeInterval(hours, time);

  const { dr: drPos, ep: epPos } = estimatedPosition({
    from: start,
    headingDeg,
    speedKnots,
    hours,
    tidalSet,
    tidalRate,
    leewayDeg,
  });

  const startPt: PositionPoint = {
    id: newId('start'),
    kind: 'position_point',
    pointType: 'assumed',
    position: start,
    time,
    label: params.startLabel ?? 'Origin',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };

  const waterTrack: Vector = {
    id: newId('wt'),
    kind: 'water_track',
    start: startPt.id,
    directionDeg: headingDeg,
    rateKnots: speedKnots,
    interval,
    source: { method: 'manual' },
    parents: [startPt.id],
    children: [],
    userPlaced: false,
  };

  const dr: PositionPoint = {
    id: newId('dr'),
    kind: 'position_point',
    pointType: 'dr',
    position: drPos,
    time,
    label: 'DR',
    source: { method: 'manual' },
    parents: [startPt.id, waterTrack.id],
    children: [],
    userPlaced: false,
  };

  // The tidal vector is applied FROM the DR, by convention.
  const tidalVector: Vector = {
    id: newId('tide'),
    kind: 'tidal_vector',
    start: dr.id,
    directionDeg: tidalSet,
    rateKnots: tidalRate,
    interval,
    label: 'Tide',
    source: { method: 'manual' },
    parents: [dr.id],
    children: [],
    userPlaced: false,
  };

  const ep: PositionPoint = {
    id: newId('ep'),
    kind: 'position_point',
    pointType: 'ep',
    position: epPos,
    time,
    label: 'EP',
    source: { method: 'manual' },
    parents: [startPt.id, waterTrack.id, dr.id, tidalVector.id],
    children: [],
    userPlaced: false,
  };

  // The ground track is the resulting track over the ground, start to EP. It is
  // derived from the start and the EP, so it is a child of the EP, which keeps
  // the graph acyclic (the EP does not also list the ground track as a parent).
  const groundTrack: Vector = {
    id: newId('gt'),
    kind: 'ground_track',
    start: startPt.id,
    directionDeg: rhumbBearingDeg(start, epPos),
    distanceNm: rhumbDistanceNm(start, epPos),
    interval,
    source: { method: 'manual' },
    parents: [startPt.id, ep.id],
    children: [],
    userPlaced: false,
  };

  // The EP's honest doubt: an uncertainty ellipse that grows with the run, from
  // speed/log error, tidal-rate error and the heading/set angle errors.
  const u = epUncertaintyEllipse({ hours, headingDeg, stwKn: speedKnots, tidalRateKn: tidalRate });
  const ellipse: Zone = {
    id: newId('epz'),
    kind: 'uncertainty_zone',
    polygon: ellipsePolygon(epPos, u),
    uncertaintyNm: u.semiMajorNm,
    label: `EP error ±${u.semiMajorNm.toFixed(1)} nm`,
    source: { method: 'manual' },
    parents: [ep.id],
    children: [],
    userPlaced: false,
  };

  return { start: startPt, waterTrack, dr, tidalVector, ep, groundTrack, ellipse };
}
